import { Suspense } from "react";
import CommonRoutesWrapper from "components/CommonRoutesWrapper";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router";
import Box from "@mui/material/Box";
import { useOktaAuth } from "@okta/okta-react";

import { useConfigContext } from "shared/contexts/ConfigContext";
import { useViewport } from "shared/contexts/ViewportContext";

import AppLayout from "features/layout/AppLayout";
import AppLoadError from "features/layout/AppLoadError";
import ErrorBoundary from "features/layout/ErrorBoundary";
import PageLoadingFallback from "features/layout/PageLoadingFallback";

import { oktaAuth } from "services/okta";
import { redirects, routes } from "services/routes";
import { useRouteDataProps } from "services/routes/hooks";
import { RouteConfig, routesConfig } from "services/routesConfig";

import RouteMiddleware from "./services/routes/RouteMiddleware";

const DEFAULT_ROUTE_VISIBILITY = true;

export const RouteComponent = ({ config }: { config: RouteConfig }) => {
  const { isMobile } = useViewport();
  const data = useRouteDataProps();

  const isVisible = config.shouldDisplay
    ? config.shouldDisplay(data)
    : DEFAULT_ROUTE_VISIBILITY;
  if (!isVisible) {
    return <RouteComponent config={routesConfig.notFound} />;
  }

  // RouteConfig has exactly one of component or componentFunction defined
  if (config.insecure) {
    return config.componentFunction
      ? config.componentFunction(data)
      : config.component;
  }

  const content = (
    <Suspense fallback={<PageLoadingFallback />}>
      {config.componentFunction
        ? config.componentFunction(data)
        : config.component}
    </Suspense>
  );

  const shouldBypassLayoutForMobileRootRedirect =
    config.path === routes.root &&
    isMobile &&
    Boolean(data.general.rootPageMobile);

  return (
    <RouteMiddleware>
      {config.noLayout || shouldBypassLayoutForMobileRootRedirect ? (
        <Box className="h-dvh">
          <ErrorBoundary>{content}</ErrorBoundary>
        </Box>
      ) : (
        <AppLayout routeConfig={config}>{content}</AppLayout>
      )}
    </RouteMiddleware>
  );
};

const AppRoutes = () => {
  const { loaded: configLoaded, error: configError } = useConfigContext();
  const { authState } = useOktaAuth();

  // Block rendering when ALL of these are true:
  // 1. Config aren't loaded yet (!configLoaded)
  // 2. User is authenticated OR auth state is still unknown
  // 3. We're NOT in the middle of an Okta login redirect (!oktaAuth.isLoginRedirect())
  //
  // This ensures:
  // - We prevent 404 errors on page refresh for authenticated users
  // - We allow the login flow to proceed for unauthenticated users
  // - We allow the login callback to complete during the redirect flow
  if (
    !configLoaded &&
    authState?.isAuthenticated !== false &&
    !oktaAuth.isLoginRedirect()
  ) {
    // The API is down (or unreachable) so the app shell never loads. Show an
    // error instead of a blank screen once a request actually fails. See SVC-4474.
    if (configError) {
      return <AppLoadError error={configError} />;
    }

    return null;
  }

  return <RouterProvider router={getAppRouter()} />;
};

export const getRouterConfig = () => {
  // We used to filter out routes that should not be displayed based on the shouldDisplay
  // function here, but that caused issues with redirects and route matching.
  // Instead, we will handle route visibility within the RouteComponent itself,
  // which allows us to still render the route and trigger redirects as needed,
  // while also ensuring that the correct component is rendered for 404s and other edge cases.
  const displayedRoutes = Object.entries(routesConfig);

  const routerRedirects = Object.entries(redirects).map(([from, to]) => ({
    path: from,
    element: <Navigate to={to} replace />,
  }));

  const securedRoutes = displayedRoutes
    .filter(([, config]) => !config.insecure)
    .map(([, config]) => ({
      path: config.path,
      element: <RouteComponent config={config} />,
    }));

  const insecureRoutes = displayedRoutes
    .filter(([, config]) => config.insecure)
    .map(([, config]) => ({
      path: config.path,
      element: <RouteComponent config={config} />,
    }));

  const wildcardRoutes = [
    {
      path: "*",
      element: <RouteComponent config={routesConfig.notFound} />,
    },
  ];

  return [
    ...routerRedirects,
    ...insecureRoutes,
    {
      element: <CommonRoutesWrapper />,
      children: [...securedRoutes, ...wildcardRoutes],
    },
  ];
};

let appRouter: ReturnType<typeof createBrowserRouter> | undefined;

// Created once, outside React state: async work that started on one router navigates with that router, so a router
// rebuilt mid-flight would change the URL without rendering the page. Strict Mode would also build a second one.
const getAppRouter = () => {
  appRouter ??= createBrowserRouter(getRouterConfig());

  return appRouter;
};

export default AppRoutes;
